import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Card from '../components/Card';
import { logger } from '../utils/logger';

interface Activity {
  _id: string;
  action: 'create' | 'update' | 'delete';
  entityType: string;
  details: string;
  createdAt: string;
}

const Home: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [itemCount, setItemCount] = useState(0);
  const [memberCount, setMemberCount] = useState(0);
  const [activityCount, setActivityCount] = useState(0);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // Always start with loading state
        if (isMounted) setIsLoading(true);

        // If no user, try to refresh once
        if (!user) {
          try {
            await refreshUser();
          } catch (error) {
            logger.error('Error refreshing user data:', error);
            if (isMounted) {
              setIsLoading(false);
              // Reset all states when there's no user
              setItemCount(0);
              setMemberCount(0);
              setActivityCount(0);
              setRecentActivities([]);
            }
          }
          return;
        }

        // If user has no family groups, show empty state
        if (!user.familyGroups?.length) {
          if (isMounted) {
            setItemCount(0);
            setMemberCount(0);
            setActivityCount(0);
            setRecentActivities([]);
            setIsLoading(false);
          }
          return;
        }

        const familyGroupId = user.familyGroups[0];
        const [itemsResponse, groupResponse, activityResponse] = await Promise.all([
          api.get(`/items/family/${familyGroupId}`),
          api.get(`/family-groups/${familyGroupId}`),
          api.get('/activity')
        ]);

        if (isMounted) {
          setItemCount(Array.isArray(itemsResponse.data) ? itemsResponse.data.length : 0);
          setMemberCount(groupResponse.data?.members?.length || 0);
          setActivityCount(activityResponse.data?.length || 0);
          setRecentActivities(activityResponse.data.slice(0, 5));
        }
      } catch (error) {
        logger.error('Error fetching dashboard data:', error);
        // Reset states on error
        if (isMounted) {
          setItemCount(0);
          setMemberCount(0);
          setActivityCount(0);
          setRecentActivities([]);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user, refreshUser]);

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'create':
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'update':
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'delete':
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getActivityColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'bg-green-500';
      case 'update':
        return 'bg-blue-500';
      case 'delete':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
      'day'
    );
  };

  return (
    <div className="py-6">
      <div className="w-full px-6">
        <Card>
          <div className="px-6 py-5 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          </div>

          <div className="p-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Items Card */}
              <Card>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 bg-[#6366F1] rounded-lg p-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-500">Total Items</h3>
                      <p className="text-3xl font-semibold text-gray-900">
                        {isLoading ? '...' : itemCount}
                      </p>
                    </div>
                  </div>
                  <Link to="/items" className="text-sm font-medium text-[#6366F1] hover:text-[#4F46E5]">
                    View inventory
                  </Link>
                </div>
              </Card>

              {/* Members Card */}
              <Card>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 bg-[#22C55E] rounded-lg p-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-500">Group Members</h3>
                      <p className="text-3xl font-semibold text-gray-900">
                        {isLoading ? '...' : memberCount}
                      </p>
                    </div>
                  </div>
                  <Link to="/groups" className="text-sm font-medium text-[#6366F1] hover:text-[#4F46E5]">
                    Manage group
                  </Link>
                </div>
              </Card>

              {/* Activity Card */}
              <Card>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 bg-[#EAB308] rounded-lg p-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-500">Recent Activity</h3>
                      <p className="text-3xl font-semibold text-gray-900">
                        {isLoading ? '...' : activityCount}
                      </p>
                    </div>
                  </div>
                  <Link to="/activity" className="text-sm font-medium text-[#6366F1] hover:text-[#4F46E5]">
                    View activity
                  </Link>
                </div>
              </Card>
            </div>

            {/* Recent Activity Feed */}
            <Card>
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Recent Activity</h3>
                <p className="mt-1 text-sm text-gray-500">A list of recent changes to your inventory</p>
              </div>
              <div className="bg-white">
                <ul role="list" className="divide-y divide-gray-200">
                  {isLoading ? (
                    <div className="p-4 text-center text-gray-500">Loading activities...</div>
                  ) : (
                    <div className="flow-root">
                      <ul role="list" className="-mb-8 px-6 py-4">
                        {recentActivities.length > 0 ?
                          recentActivities.map((activity, index) => (
                            <li key={activity._id}>
                              <div className="relative pb-8">
                                {index < recentActivities.length - 1 ? (
                                  <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                                ) : null}
                                <div className="relative flex space-x-3">
                                  <div>
                                    <span className={`h-8 w-8 rounded-full ${getActivityColor(activity.action)} flex items-center justify-center ring-8 ring-white`}>
                                      {getActivityIcon(activity.action)}
                                    </span>
                                  </div>
                                  <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                    <div>
                                      <p className="text-sm text-gray-900">
                                        {activity.action.charAt(0).toUpperCase() + activity.action.slice(1)} {activity.entityType}: {activity.details}
                                      </p>
                                    </div>
                                    <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                      <time dateTime={activity.createdAt}>{formatDate(activity.createdAt)}</time>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </li>
                          ))
                        : (
                          <div className="p-4 text-center text-gray-500">No recent activity</div>
                        )}
                      </ul>
                    </div>
                  )}
                </ul>
              </div>
            </Card>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Home; 